import { Box, Grid, Typography } from '@mui/material';

import { FormCard } from '@/components';
import { useKaseContext } from '@/hooks';
import { IProperty } from '@/models';
import { useKaseMainContext } from '@/pages/Kases/KaseEdit/KaseMainContext';
import {
  formatPersonName,
  getFormattedDateTime,
  getMasterNameDisplayName,
} from '@/utils';

import {
  IPropertyDetailField,
  PropertyDetailsGeneralFields,
  PropertyTypeSpecificFields,
} from './data';

interface PropertyDetailsCardProps {
  property: IProperty;
  caseId?: string;
  cfsId?: string;
  propertyTypeLabel?: string;
  length?: boolean;
}

const getFieldValue = (
  property: IProperty,
  field: IPropertyDetailField,
  caseId?: string,
  cfsId?: string,
  propertyTypeLabel?: string,
  PropertyInvolvementList?: any,
): string | null => {
  switch (field.key) {
    case 'number':
      return property.number || null;
    case 'name':
      return property.name || null;
    case 'typeTag':
      return property.typeTag || null;
    case 'involvement':
      return PropertyInvolvementList.get(property.involvement)?.label || null;
    case 'description':
      return property.description || null;
    case 'owner':
      return property.owner ? getMasterNameDisplayName(property.owner) : null;
    case 'value':
      return property.value != null ? String(property.value) : null;
    case 'loggedInfo': {
      const info = property.loggedInfo;
      if (!info) return null;
      const name = formatPersonName(info.loggedBy);
      const date = info.loggedDate
        ? getFormattedDateTime(new Date(info.loggedDate).toISOString())
        : '';
      return [name, date].filter(Boolean).join(' - ') || null;
    }
    case 'inboundTransferredInfo': {
      const info = property.inboundTransferredInfo;
      if (!info) return null;
      const name = formatPersonName(info.transferredBy);
      const date = info.transferredDate
        ? getFormattedDateTime(new Date(info.transferredDate).toISOString())
        : '';
      return [name, date].filter(Boolean).join(' - ') || null;
    }
    case 'type':
      return propertyTypeLabel || property.type || null;
    case 'caseId':
      return caseId || null;
    case 'cfsId':
      return cfsId || null;
    default:
      return null;
  }
};

const getTypeSpecificValue = (
  property: IProperty,
  fieldKey: string,
  drugTypes?: any,
): string | null => {
  switch (fieldKey) {
    case 'cash.amount':
      return property.cash?.amount != null
        ? String(property.cash.amount)
        : null;
    case 'cash.currency':
      return property.cash?.currency || null;
    case 'jewelry.type':
      return property.jewelry?.type?.type || null;
    case 'alcohol.brand':
      return property.alcohol?.brand?.name || null;
    case 'drugs.type':
      return (
        property.drugs
          ?.map((drug) =>
            drug.type ? drugTypes.get(drug.type?.toString())?.label : false,
          )
          .filter(Boolean)
          .join(', ') || null
      );
    case 'drugs.quantity':
      return (
        property.drugs
          ?.map((drug) =>
            drug.quantity != null
              ? `${drug.quantity}${
                  drug.measurementType ? ` ${drug.measurementType}` : ''
                }`
              : null,
          )
          .filter(Boolean)
          .join(', ') || null
      );
    case 'firearm.type':
      return property.firearm?.type || null;
    case 'firearm.make':
      return property.firearm?.make?.name || null;
    case 'firearm.model':
      return property.firearm?.model?.name || null;
    case 'knife.make':
      return property.knife?.make?.name || null;
    case 'knife.model':
      return property.knife?.model?.name || null;
    case 'explosive.type':
      return property.explosive?.type || null;
    case 'vehicle.plateNumber':
      return property.vehicle?.masterVehicle?.licensePlate?.number || null;
    case 'vehicle.make':
      return property.vehicle?.masterVehicle?.make || null;
    case 'vehicle.model':
      return property.vehicle?.masterVehicle?.model || null;
    case 'vehicle.color':
      return property.vehicle?.masterVehicle?.color?.name || null;
    default:
      return null;
  }
};

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) => {
  if (value == null) return null;

  return (
    <Box sx={{ minWidth: 0, px: 2, py: 0.5 }}>
      <Typography
        variant="body1"
        fontWeight={500}
        fontSize={14}
        sx={{ color: 'text.secondary', display: 'block' }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontSize={14}
        sx={{ color: 'text.primary', wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const PropertyDetailsCard = ({
  property,
  caseId,
  propertyTypeLabel,
}: PropertyDetailsCardProps) => {
  const typeSpecificKey = Object.keys(PropertyTypeSpecificFields).find(
    (key) => {
      const currentValue = property[key as keyof IProperty];

      return Array.isArray(currentValue)
        ? currentValue?.length > 0
        : currentValue != null;
    },
  );
  const { kase } = useKaseContext();

  const { DrugType: drugTypes, PropertyInvolvementList } = useKaseMainContext();

  const typeSpecificFields = typeSpecificKey
    ? PropertyTypeSpecificFields[typeSpecificKey]
    : [];
  return (
    <FormCard
      sx={{
        '& .divider': {
          marginTop: '5px',
        },
      }}
      title={
        <Box
          className="cardTitle"
          style={{ padding: '5px 10px 0' }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="inherit" component="span">
            Property details
          </Typography>
          <Typography variant="inherit" component="span">
            Item No. {property.itemNumber}
          </Typography>
        </Box>
      }
    >
      <Grid
        container
        sx={{
          width: '100%',
        }}
      >
        {PropertyDetailsGeneralFields.map((field) => {
          const fieldValue = getFieldValue(
            property,
            field,
            caseId,
            kase?.cfsNumber,
            propertyTypeLabel,
            PropertyInvolvementList,
          );
          return fieldValue ? (
            <Grid item xs={3} {...field.grid} key={field.key}>
              <DetailField label={field.label} value={fieldValue} />
            </Grid>
          ) : null;
        })}
        {typeSpecificFields.map((field) => (
          <DetailField
            key={field.key}
            label={field.label}
            value={getTypeSpecificValue(property, field.key, drugTypes)}
          />
        ))}
      </Grid>
    </FormCard>
  );
};
